/**
 * Sawyer E4 (#754) — person-to-person messages.
 *
 * ≥720px: two panes (thread list 320px | the open thread). <720px: the list
 * first; a row opens the thread full-screen with "Back to messages" (AC21).
 * "New message" picks a co-worker; the first message creates the pair's
 * thread (or reuses it — one thread per pair) and opens it.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  personName,
  roleLabel,
  staffMessagesApi,
  type Person,
  type Thread,
} from '../../lib/staff-messages-api.js';
import { PeoplePicker } from './PeoplePicker.js';
import { inactiveSentence, ThreadView } from './ThreadView.js';
import { refreshUnread } from './useStaffMessagesUnread.js';
import { bodyState, Composer, LoadError, SkeletonRows, StaleLine, TimeStamp, useVisiblePoll } from './shared.js';

const LIST_REFRESH_MS = 30_000;

type Load = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; threads: Thread[] };

export interface DirectPanelProps {
  apiUrl: string;
  threadId: string | null;
  canWrite: boolean;
}

export function DirectPanel({ apiUrl, threadId, canWrite }: DirectPanelProps): JSX.Element {
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [stale, setStale] = useState(false);
  const [lastGood, setLastGood] = useState<Date | null>(null);
  const [picking, setPicking] = useState(false);
  const [composeTo, setComposeTo] = useState<Person | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const loadList = useCallback(
    async (mode: 'first' | 'quiet') => {
      if (mode === 'first') setLoad({ kind: 'loading' });
      const r = await staffMessagesApi.threads(apiUrl);
      if (!r.ok) {
        if (mode === 'quiet') setStale(true);
        else setLoad({ kind: 'error' });
        return;
      }
      setStale(false);
      setLastGood(new Date());
      setLoad({ kind: 'ready', threads: r.data.threads });
    },
    [apiUrl],
  );

  useEffect(() => {
    void loadList('first');
  }, [loadList]);
  useVisiblePoll(() => void loadList('quiet'), LIST_REFRESH_MS, load.kind === 'ready');

  const onActivity = useCallback(() => void loadList('quiet'), [loadList]);

  // Opening a thread from the route ends any compose-in-progress.
  useEffect(() => {
    if (threadId) setComposeTo(null);
  }, [threadId]);

  function pick(p: Person): void {
    setPicking(false);
    const existing = load.kind === 'ready' ? load.threads.find((t) => t.other.id === p.id) : undefined;
    if (existing) {
      navigate(`/team-messages/direct/${encodeURIComponent(existing.id)}`);
      return;
    }
    setComposeTo(p);
    setDraft('');
    setSendError(null);
    if (threadId) navigate('/team-messages/direct');
  }

  async function startThread(): Promise<void> {
    if (!composeTo) return;
    const { trimmed, empty, over } = bodyState(draft);
    if (empty || over) return;
    setSending(true);
    setSendError(null);
    const r = await staffMessagesApi.startDirect(apiUrl, composeTo.id, trimmed);
    setSending(false);
    if (!r.ok) {
      setSendError(
        r.code === 'recipient_inactive'
          ? inactiveSentence(composeTo.firstName)
          : r.code === 'recipient_not_found'
            ? `${composeTo.firstName} isn't on your team's list anymore.`
            : 'Not sent. Check your connection and try again.',
      );
      return;
    }
    setComposeTo(null);
    setDraft('');
    void loadList('quiet');
    void refreshUnread();
    navigate(`/team-messages/direct/${encodeURIComponent(r.data.thread.id)}`);
  }

  const threads = load.kind === 'ready' ? load.threads : [];
  const open = threadId !== null || composeTo !== null;
  const listEmpty = load.kind === 'ready' && threads.length === 0;

  return (
    <div className={`ferro-tm-direct${open ? ' ferro-tm-direct--open' : ''}`} data-testid="team-messages-direct">
      <div className="ferro-tm-direct__list">
        {canWrite ? (
          <button
            type="button"
            className={`ferro-btn ${listEmpty ? 'ferro-btn--primary' : 'ferro-btn--outline'} ferro-tm-direct__new`}
            onClick={() => setPicking(true)}
            data-testid="direct-new"
          >
            New message
          </button>
        ) : null}
        {stale ? <StaleLine lastGood={lastGood} /> : null}
        {load.kind === 'loading' ? <SkeletonRows count={6} variant="thread" /> : null}
        {load.kind === 'error' ? <LoadError testId="direct-error" onRetry={() => void loadList('first')} /> : null}
        {listEmpty ? (
          <div className="ferro-tm-empty" data-testid="direct-empty">
            <p>{canWrite ? 'No messages yet. Start one with anyone on your team.' : 'No messages yet.'}</p>
          </div>
        ) : null}
        {threads.length > 0 ? (
          <ul className="ferro-tm-threads" data-testid="direct-threads">
            {threads.map((t) => (
              <li key={t.id}>
                <Link
                  to={`/team-messages/direct/${encodeURIComponent(t.id)}`}
                  className={`ferro-tm-threadrow${t.id === threadId ? ' ferro-tm-threadrow--active' : ''}`}
                  aria-current={t.id === threadId ? 'page' : undefined}
                  data-testid="direct-thread-row"
                >
                  <span className="ferro-tm-threadrow__top">
                    <span className="ferro-tm-threadrow__name">{personName(t.other)}</span>
                    {t.lastMessage ? <TimeStamp iso={t.lastMessage.createdAt} /> : null}
                  </span>
                  <span className="ferro-tm-threadrow__bottom">
                    <span className="ferro-tm-threadrow__preview">
                      {roleLabel(t.other.role) ? `${roleLabel(t.other.role)} · ` : ''}
                      {t.lastMessage ? `${t.lastMessage.fromMe ? 'You: ' : ''}${t.lastMessage.preview}` : ''}
                    </span>
                    {t.unreadCount > 0 ? (
                      <span className="ferro-tm-count" aria-label={`${t.unreadCount} unread`}>
                        {t.unreadCount}
                      </span>
                    ) : null}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="ferro-tm-direct__pane">
        {threadId ? (
          <ThreadView apiUrl={apiUrl} threadId={threadId} canWrite={canWrite} onActivity={onActivity} />
        ) : composeTo ? (
          <section className="ferro-tm-thread" data-testid="direct-compose" aria-label={`New message to ${composeTo.firstName}`}>
            <div className="ferro-tm-thread__head">
              <button type="button" className="ferro-tm-back" onClick={() => setComposeTo(null)}>
                Back to messages
              </button>
              <div className="ferro-tm-thread__who">
                <h2 className="ferro-tm-thread__name">To {personName(composeTo)}</h2>
                {roleLabel(composeTo.role) ? <span className="ferro-tm__muted">{roleLabel(composeTo.role)}</span> : null}
              </div>
            </div>
            <div className="ferro-tm-thread__scroll">
              {sendError ? (
                <p className="ferro-tm-post__failed" role="alert" data-testid="direct-compose-error">
                  {sendError}
                </p>
              ) : null}
            </div>
            <div className="ferro-tm-thread__composer">
              <Composer
                testId="direct-composer"
                label={`Message to ${composeTo.firstName}`}
                placeholder={`Message ${composeTo.firstName}`}
                value={draft}
                onChange={setDraft}
                onSubmit={() => void startThread()}
                submitLabel="Send"
                rows={2}
                enterSends
                focusOnMount
                busy={sending}
              />
            </div>
          </section>
        ) : (
          <p className="ferro-tm-direct__placeholder">Pick a conversation, or start a new one.</p>
        )}
      </div>

      {picking ? <PeoplePicker apiUrl={apiUrl} onPick={pick} onClose={() => setPicking(false)} /> : null}
    </div>
  );
}
